import { useCallback, useLayoutEffect, useState } from "react";
import type { DependencyList, RefObject } from "react";

/**
 * useOverflowWidth
 * Hook que detecta si el contenido horizontal de un elemento HTML se desborda
 * respecto a su ancho visible (clientWidth).
 *
 * Parámetros
 * - ref: referencia al elemento a observar.
 * - deps: lista de dependencias opcional para volver a evaluar el desborde.
 *
 * Retorno
 * - boolean: `true` si `scrollWidth > clientWidth` (hay overflow horizontal).
 *
 *
 * Ejemplo de uso
 *
 * ```tsx
 * import { useRef } from "react";
 * import { Tooltip } from "primereact/tooltip";
 * import { useOverflowWidth } from "@orion/hooks";
 *
 * export function OverflowWithTooltip({ text }: { text: string }) {
 *   const ref = useRef<HTMLDivElement>(null);
 *   const isOverflowing = useOverflowWidth(ref, [text]);
 *
 *   return (
 *     <div>
 *       {isOverflowing && <Tooltip>{text}</Tooltip>}
 *       <div ref={ref} className="truncate">{text}</div>
 *     </div>
 *   );
 * }
 * ```
 */

export function useOverflowWidth<T extends HTMLElement>(
  ref: RefObject<T | null>,
  deps: DependencyList = [],
) {
  const [isOverflowing, setIsOverflowing] = useState(false);

  const check = useCallback(() => {
    if (!ref) return;
    const element = ref.current;
    if (!element) return;
    setIsOverflowing(element.scrollWidth > element.clientWidth);
  }, [ref]);

  useLayoutEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return isOverflowing;
}
